import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  CellClickedEvent,
  ColDef,
  GetRowNodeIdFunc,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  RowClassParams,
} from '@ag-grid-community/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import { withStyles } from '@material-ui/core';
import clsx from 'clsx';
import React, { useState } from 'react';

import { BaseActionsMenuProps } from 'src/constants';
import HeaderRender from 'src/legacy/components/AgGrid/HeaderRender';
import { BaseMenu } from 'src/legacy/components/Dropdowns/BaseMenu';
import { Loader } from 'src/legacy/components/Loading';
import NoDataOverlay, {
  OverlayParams,
} from 'src/legacy/components/Overlays/NoDataOverlay';
import NoFilesStandardPage from 'src/legacy/components/UI/StandardPage/NoFilesStandardPage';

import * as AdGridCss from '~/externalStyles/Table.scss';

ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule]);

const baseDefaultColDef = {
  sortable: false,
  suppressMenu: true,
  suppressMovable: true,
};

export interface SortParams {
  field?: string;
  sortable?: boolean;
  sort?: string;
}

export interface TableProps extends AgGridReactProps {
  animateRows?: boolean;
  immutableData?: boolean;
  rowData?: Array<Record<string, any>>;
  columnDefs: Array<ColDef>;
  rowClass?: string;
  suppressNoRowsOverlay?: boolean;
  defaultColDef?: ColDef;
  gridLoaded?: (gridApi: GridApi) => void;
  searchKey?: string;
  enableCellTextSelection?: boolean;
  rowSelection?: string;
  rowHeight?: number;
  headerHeight?: number;
  overlayParams?: OverlayParams;
  pagination?: boolean;
  paginationAutoPageSize?: boolean;
  onRowClicked?: (rowData: any) => void;
  frameworkComponents: any;
  noRowsOverlayComponentName?: string;
  suppressCellSelection?: boolean;
  getRowNodeId?: GetRowNodeIdFunc;
  className?: string;
  onCellClicked?: AgGridReactProps['onCellClicked'];
  disableRowHover?: boolean;
  getRowClass?: (params: RowClassParams) => string;
  onCreateNewRow?: (params: GridReadyEvent) => void;
}

interface AgGridContextType {
  gridApi?: GridApi | null;
  setGridApi: (gridApi?: GridApi) => void;
}

// set the defaults
export const AgGridContext = React.createContext({
  gridApi: null,
  setGridApi: () => {
    console.info('setGridApi not provided');
  },
} as AgGridContextType);

const tableContainerStyle = {
  height: '100%',
  width: '100%',
};

const NoFilesStandardPageRenderer = ({
  onActionButtonOptionSelected,
  isFolderLockedForClient,
}: Pick<
  OverlayParams,
  'onActionButtonOptionSelected' | 'isFolderLockedForClient'
>) =>
  !onActionButtonOptionSelected ? null : (
    <NoFilesStandardPage
      onOptionSelected={onActionButtonOptionSelected}
      isFolderLockedForClient={isFolderLockedForClient}
    />
  );

const Table: React.FC<TableProps> = ({
  gridLoaded,
  rowData,
  columnDefs,
  searchKey,
  onRowClicked,
  frameworkComponents,
  noRowsOverlayComponentName = '',
  defaultColDef = baseDefaultColDef,
  enableCellTextSelection = true,
  rowSelection = 'multiple',
  rowHeight = 50,
  headerHeight = 40,
  disableRowHover = false,
  overlayParams = {
    noRowsLabel: 'No data is added',
  },
  onCreateNewRow,
  getRowClass,
  ...tableProps
}) => {
  const gridTableApi = React.useRef<GridApi>();
  const [actionMenuButtonProps, setActionMenuButtonProps] =
    useState<BaseActionsMenuProps | null>(null);
  const [showPinnedColumnShadow, setShowPinnedColumnShadow] = useState(false);
  const { setGridApi, gridApi } = React.useContext(AgGridContext);

  const handleActionMenuClick = React.useCallback((menuOptions: any) => {
    setActionMenuButtonProps({
      ...menuOptions,
      menuProps: {
        ...menuOptions.menuProps,
        onClose: () => setActionMenuButtonProps(null),
      },
    });
  }, []);

  const formattedColDefs = React.useMemo(
    () =>
      columnDefs.map((col) => {
        if (col.headerName === 'Actions') {
          return {
            width: 48, // Set a fixed width for the actions column. This can be overridden in the column definition.
            headerClass: 'hiddenHeaderCell',
            cellClass: 'centeredColumn',
            ...col,
            cellRendererParams: {
              onActionMenuClick: handleActionMenuClick,
              ...col.cellRendererParams,
            },
          };
        }

        if (col.headerName === 'Creation date') {
          return {
            headerClass: 'centeredHeaderCell',
            ...col,
          };
        }

        return col;
      }),
    [columnDefs, handleActionMenuClick],
  );

  React.useEffect(() => {
    if (gridApi) {
      gridApi.setQuickFilter(searchKey ?? '');
    }
  }, [searchKey, gridApi]);

  const mappedFrameworkComponents = React.useMemo(
    () => ({
      ...frameworkComponents,
      agColumnHeader: HeaderRender,
      customLoadingOverlay: Loader,
      customNoRowsOverlay: NoDataOverlay,
      NoFilesStandardPage: NoFilesStandardPageRenderer,
    }),
    [frameworkComponents],
  );

  const baseColDef = React.useMemo(
    () => ({ ...baseDefaultColDef, ...defaultColDef }),
    [defaultColDef],
  );

  const handleCellClicked = React.useCallback(
    (event: CellClickedEvent) => {
      if (!onRowClicked) {
        return;
      }

      if (event.colDef.headerName !== 'Actions') {
        // if this is not the actions column which has its own click handler
        onRowClicked(event.data);
      }
    },
    [onRowClicked],
  );

  const handleShowPinnedColumnShadow = React.useCallback(() => {
    const gridCenter = document.querySelector('.ag-center-cols-viewport');
    if (!gridCenter) {
      return;
    }
    // Show shadow only if horizontal scrolling is possible (i.e., content overflows)
    setShowPinnedColumnShadow(
      gridCenter && gridCenter.scrollWidth > gridCenter.clientWidth,
    );
  }, []);

  const handleResize = React.useCallback(() => {
    setTimeout(() => {
      if (gridTableApi.current) {
        gridTableApi.current.sizeColumnsToFit();
        handleShowPinnedColumnShadow();
      }
    });
  }, [handleShowPinnedColumnShadow]);

  // Manage resize event listener lifecycle - only add after grid is ready (gridApi is set)
  // and clean up on unmount to prevent memory leaks
  React.useEffect(() => {
    if (!gridApi) return;
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [gridApi, handleResize]);

  const onGridReady = React.useCallback(
    (params: GridReadyEvent) => {
      gridTableApi.current = params.api;
      setGridApi(params.api);
      params.api.sizeColumnsToFit();
      if (gridLoaded) {
        return gridLoaded(params.api);
      }
      if (onCreateNewRow) {
        return onCreateNewRow(params);
      }
      return null;
    },
    [gridLoaded, onCreateNewRow, setGridApi],
  );

  /**
   * fit columns when changed
   */
  const handleColumnsChanged = React.useCallback(() => {
    if (gridTableApi.current) {
      gridTableApi.current.sizeColumnsToFit();
      handleShowPinnedColumnShadow();
    }
  }, [handleShowPinnedColumnShadow]);

  return (
    <div
      className={clsx('ag-theme-portal', {
        'disable-hover-effect': disableRowHover,
        'show-sticky-cells-shadow': showPinnedColumnShadow,
      })}
      style={tableContainerStyle}
    >
      <AgGridReact
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        defaultColDef={baseColDef}
        frameworkComponents={mappedFrameworkComponents}
        columnDefs={formattedColDefs}
        onGridReady={onGridReady}
        suppressScrollOnNewData
        suppressAggFuncInHeader
        noRowsOverlayComponent={
          noRowsOverlayComponentName || 'customNoRowsOverlay'
        }
        loadingOverlayComponent="customLoadingOverlay"
        noRowsOverlayComponentParams={{
          overlayParams: () => overlayParams,
        }}
        suppressContextMenu
        rowData={rowData}
        rowSelection={rowSelection}
        enableCellTextSelection={enableCellTextSelection}
        onCellClicked={handleCellClicked}
        onGridColumnsChanged={handleColumnsChanged}
        animateRows
        suppressRowClickSelection
        getRowClass={getRowClass}
        {...tableProps}
      />
      {actionMenuButtonProps && <BaseMenu {...actionMenuButtonProps} />}
    </div>
  );
};
export default withStyles(AdGridCss)(Table);
